import { useState, useCallback } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { toast } from "sonner";
import { Upload, X, FileText, CreditCard, User, Check, AlertCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  {
    id: "government_id",
    label: "Government ID",
    description: "Driver's License, State ID, or Passport",
    icon: User,
    required: true,
    acceptedFormats: ".pdf,.jpg,.jpeg,.png"
  },
  {
    id: "social_security_card",
    label: "Social Security Card",
    description: "Front of your Social Security card",
    icon: CreditCard,
    required: true,
    acceptedFormats: ".pdf,.jpg,.jpeg,.png"
  },
  {
    id: "proof_of_address",
    label: "Proof of Address",
    description: "Utility bill, bank statement, or lease agreement",
    icon: FileText,
    required: false,
    acceptedFormats: ".pdf,.jpg,.jpeg,.png"
  },
  {
    id: "additional_documents",
    label: "Additional Documents",
    description: "Police reports, fraud affidavits, or other supporting documents",
    icon: FileText,
    required: false,
    acceptedFormats: ".pdf,.jpg,.jpeg,.png"
  }
];

export default function SupportingDocumentsPage({ 
  clientId, 
  clientData,
  onDocumentsChange, 
  savedDocuments = {},
  onIdUploaded,
  onSsnUploaded,
  onComplete,
  onNext,
  onPrevious,
  idDocumentUploaded,
  ssnCardUploaded
}) {
  const [documents, setDocuments] = useState(savedDocuments);
  const [uploading, setUploading] = useState({});
  const [errors, setErrors] = useState({});

  const handleFileSelect = useCallback(async (docType, file) => {
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [docType]: "File size must be less than 10MB" }));
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [docType]: "Only JPG, PNG, and PDF files are allowed" }));
      return;
    }

    setUploading(prev => ({ ...prev, [docType]: true }));
    setErrors(prev => ({ ...prev, [docType]: null }));

    try {
      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      formData.append('client_id', clientId);

      // Upload to backend
      const response = await axios.post(`${API}/api/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const docData = {
        id: response.data?.id || `${docType}_${Date.now()}`,
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        url: response.data?.url || URL.createObjectURL(file),
        localPreview: URL.createObjectURL(file)
      };

      setDocuments(prev => {
        const updated = { ...prev, [docType]: docData };
        if (onDocumentsChange) onDocumentsChange(updated);
        // Call the parent callbacks for ID and SSN
        if (docType === 'government_id' && onIdUploaded) onIdUploaded();
        if (docType === 'social_security_card' && onSsnUploaded) onSsnUploaded();
        return updated;
      });

      toast.success(`${DOCUMENT_TYPES.find(d => d.id === docType)?.label} uploaded successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      
      // Still save locally for preview even if upload fails
      const docData = {
        id: `local_${docType}_${Date.now()}`,
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        localPreview: URL.createObjectURL(file),
        pendingUpload: true
      };

      setDocuments(prev => {
        const updated = { ...prev, [docType]: docData };
        if (onDocumentsChange) onDocumentsChange(updated);
        // Call the parent callbacks even for local saves
        if (docType === 'government_id' && onIdUploaded) onIdUploaded();
        if (docType === 'social_security_card' && onSsnUploaded) onSsnUploaded();
        return updated;
      });

      toast.warning(`${file.name} saved locally. Will be included in final package.`);
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  }, [clientId, onDocumentsChange]);

  const removeDocument = (docType) => {
    setDocuments(prev => {
      const updated = { ...prev };
      if (updated[docType]?.localPreview) {
        URL.revokeObjectURL(updated[docType].localPreview);
      }
      delete updated[docType];
      if (onDocumentsChange) onDocumentsChange(updated);
      return updated;
    });
    toast.success("Document removed");
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const requiredDocs = DOCUMENT_TYPES.filter(d => d.required);
  const hasRequiredDocs = requiredDocs.every(d => documents[d.id]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Supporting Documents</h2>
        <p className="text-zinc-400">
          Upload your identification documents to include in the dispute package
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {DOCUMENT_TYPES.map((docType) => {
          const Icon = docType.icon;
          const doc = documents[docType.id];
          const isUploading = uploading[docType.id];
          const error = errors[docType.id];

          return (
            <Card 
              key={docType.id} 
              className={`bg-zinc-900 border-zinc-800 ${doc ? 'border-green-500/50' : docType.required ? 'border-orange-500/30' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`p-2 rounded-lg ${doc ? 'bg-green-500/20' : 'bg-zinc-800'}`}>
                    <Icon className={`h-5 w-5 ${doc ? 'text-green-400' : 'text-zinc-400'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{docType.label}</h3>
                      {docType.required && !doc && (
                        <span className="text-xs text-orange-400 bg-orange-500/20 px-2 py-0.5 rounded">Required</span>
                      )}
                      {doc && <Check className="h-4 w-4 text-green-400" />}
                    </div>
                    <p className="text-sm text-zinc-400">{docType.description}</p>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm mb-3">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </div>
                )}

                {doc ? (
                  <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      {doc.localPreview && doc.type?.startsWith('image/') ? (
                        <img 
                          src={doc.localPreview} 
                          alt={doc.name} 
                          className="h-12 w-12 object-cover rounded"
                        />
                      ) : (
                        <FileText className="h-8 w-8 text-zinc-400" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-white truncate max-w-[150px]">{doc.name}</p>
                        <p className="text-xs text-zinc-500">{formatFileSize(doc.size)}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeDocument(docType.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="block">
                    <input
                      type="file"
                      accept={docType.acceptedFormats}
                      onChange={(e) => handleFileSelect(docType.id, e.target.files?.[0])}
                      className="hidden"
                      disabled={isUploading}
                    />
                    <div className={`border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center cursor-pointer hover:border-orange-500/50 hover:bg-zinc-800/50 transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      {isUploading ? (
                        <div className="animate-spin h-6 w-6 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-2" />
                      ) : (
                        <Upload className="h-6 w-6 text-zinc-500 mx-auto mb-2" />
                      )}
                      <p className="text-sm text-zinc-400">
                        {isUploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">JPG, PNG or PDF up to 10MB</p>
                    </div>
                  </label>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary */}
      <div className="bg-zinc-800/50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-400">
              {Object.keys(documents).length} of {DOCUMENT_TYPES.length} documents uploaded
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              ID and SSN Card are recommended to strengthen your dispute package
            </p>
          </div>
          <div className="flex gap-2">
            {onPrevious && (
              <Button variant="outline" onClick={onPrevious} className="border-zinc-700">
                Previous
              </Button>
            )}
            {onComplete && (
              <Button 
                onClick={onComplete}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Continue to Review
              </Button>
            )}
            {onNext && !onComplete && (
              <Button 
                onClick={onNext}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
